import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LANDING } from '@/constants/testIds';
import { joinWaitlist, getWaitlistCount } from '@/lib/api';

export default function Waitlist() {
  const [form, setForm] = useState({ name: '', email: '' });
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [message, setMessage] = useState('');
  const [count, setCount] = useState(null);

  useEffect(() => {
    getWaitlistCount()
      .then((d) => setCount(d))
      .catch(() => {});
  }, []);

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      setStatus('error');
      setMessage('Please add your name and email.');
      return;
    }
    setStatus('loading');
    setMessage('');
    try {
      await joinWaitlist({ name: form.name.trim(), email: form.email.trim() });
      const c = await getWaitlistCount().catch(() => null);
      if (c) setCount(c);
      setStatus('success');
      setMessage("You're on the list. We'll be in touch.");
      setForm({ name: '', email: '' });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        'Something went wrong. Please try again.';
      setStatus('error');
      setMessage(String(detail));
    }
  }

  const remaining = count?.remaining ?? null;

  return (
    <section
      id="waitlist"
      className="relative bg-[#0E0E0E] text-[#FAF8F4] py-32 lg:py-48 overflow-hidden"
    >
      {/* glow */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80vw] h-[80vw] rounded-full bg-[#CBBBA0]/10 blur-[140px]" />
      </div>

      <div className="relative max-w-2xl mx-auto px-6 lg:px-12 text-center">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="text-[11px] tracking-[0.35em] uppercase text-[#CBBBA0] mb-8"
        >
          Chapter 09 · Early Access
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.1 }}
          className="font-display text-[clamp(2.75rem,7vw,6rem)] leading-[0.95] tracking-[-0.03em]"
        >
          Join The <span className="font-serif italic text-[#CBBBA0]">First 100.</span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.3 }}
          className="mt-6 text-[16px] leading-relaxed text-[#FAF8F4]/65 max-w-md mx-auto"
        >
          Be the first to experience KIVO. Early-access pricing, signed by the founders,
          shipped before the rest of the world.
        </motion.p>

        {remaining !== null && (
          <motion.div
            data-testid={LANDING.waitlistCount}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.45 }}
            className="mt-6 inline-flex items-center gap-3 text-[11px] tracking-[0.3em] uppercase text-[#CBBBA0]/80"
          >
            <span className="h-px w-8 bg-[#CBBBA0]/40" />
            {remaining > 0 ? `${remaining} spots remaining` : 'Waitlist full — joining queue'}
            <span className="h-px w-8 bg-[#CBBBA0]/40" />
          </motion.div>
        )}

        <motion.form
          data-testid={LANDING.waitlistForm}
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.55 }}
          className="mt-14 max-w-md mx-auto text-left space-y-8"
        >
          <div>
            <label className="block text-[11px] tracking-[0.3em] uppercase text-[#FAF8F4]/50 mb-3">
              Your Name
            </label>
            <input
              data-testid={LANDING.waitlistName}
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Aanya Mehra"
              className="w-full bg-transparent border-b border-[#FAF8F4]/25 pb-3 text-lg text-[#FAF8F4] placeholder-[#FAF8F4]/30 focus:outline-none focus:border-[#CBBBA0] transition-colors"
            />
          </div>

          <div>
            <label className="block text-[11px] tracking-[0.3em] uppercase text-[#FAF8F4]/50 mb-3">
              Your Email
            </label>
            <input
              data-testid={LANDING.waitlistEmail}
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="aanya@kivo.world"
              className="w-full bg-transparent border-b border-[#FAF8F4]/25 pb-3 text-lg text-[#FAF8F4] placeholder-[#FAF8F4]/30 focus:outline-none focus:border-[#CBBBA0] transition-colors"
            />
          </div>

          <button
            data-testid={LANDING.waitlistSubmit}
            type="submit"
            disabled={status === 'loading'}
            className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#FAF8F4] text-[#111] text-[13px] tracking-[0.2em] uppercase px-10 py-5 rounded-full hover:bg-[#CBBBA0] hover:text-[#111] transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {status === 'loading' ? 'Sending…' : 'Get Early Access'}
            <span className="transition-transform group-hover:translate-x-1">→</span>
          </button>

          <AnimatePresence>
            {status === 'success' && (
              <motion.div
                key="ok"
                data-testid={LANDING.waitlistSuccess}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="text-[13px] text-[#CBBBA0] border-l border-[#CBBBA0] pl-4"
              >
                {message}
              </motion.div>
            )}
            {status === 'error' && (
              <motion.div
                key="err"
                data-testid={LANDING.waitlistError}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="text-[13px] text-[#E8B4B4] border-l border-[#E8B4B4] pl-4"
              >
                {message}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.form>

        <div className="mt-20 text-[10px] tracking-[0.35em] uppercase text-[#FAF8F4]/30">
          KIVO · Original by Choice.
        </div>
      </div>
    </section>
  );
}
